import axios from 'axios';

// Get API URL from environment variable (set at build time) or runtime config
const getApiUrl = () => {
  // First try environment variable (set during build)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL + '/api/v1';
  }
  // Default fallback for local development
  return 'http://localhost:8000/api/v1';
};

// Create axios instance with API prefix
const api = axios.create({
  baseURL: getApiUrl(),
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor to add auth token and tenant context
api.interceptors.request.use(
  (config) => {
    // Add auth token - ONLY use 'accessToken' key (login stores here)
    // Tenant-admin mode for a superadmin is isolated to one browser window.
    // Prefer its sessionStorage token without changing the platform token
    // shared through localStorage with the original platform window.
    const token = sessionStorage.getItem('accessToken') || localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add tenant ID header for tenant isolation
    const userStr = sessionStorage.getItem('user') || localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user.tenant_id) {
          config.headers['X-Tenant-ID'] = user.tenant_id;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only logout on 401 Unauthorized errors that indicate token is invalid
    // Don't logout on 400 (bad request), 403 (forbidden), or other errors
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || '';
      const isLoginAttempt = requestUrl.includes('/auth/login');
      // If we're already sitting on the login/auth screen, NEVER hard-redirect
      // to /login. A bad-credentials login returns 401 "Invalid credentials";
      // reloading the page here wipes the on-screen error dialog before the user
      // can read it (that reload IS the "why won't it show my login error" bug).
      // The user must be able to read/dismiss the error and retry in place.
      const path = (typeof window !== 'undefined' && window.location.pathname) || '';
      const onAuthPage = path === '/login' || path.startsWith('/auth');
      const errorDetail = error.response?.data?.detail || '';
      // Only auto-logout+redirect on a genuine expired/invalid SESSION token,
      // and only when NOT a login attempt and NOT already on the auth page.
      if (!isLoginAttempt && !onAuthPage && (
          errorDetail.includes('token') ||
          errorDetail.includes('expired') ||
          errorDetail.includes('Invalid') ||
          errorDetail.includes('Not authenticated') ||
          errorDetail === 'Could not validate credentials')) {
        const tenantAdminTab = sessionStorage.getItem('tenantAdminMode') === 'true';
        if (!tenantAdminTab) {
          localStorage.removeItem('token');
          localStorage.removeItem('accessToken');
          localStorage.removeItem('user');
        }
        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('tenantAdminMode');
        stopTokenRefresh();  // Stop refresh interval on logout
        if (tenantAdminTab) {
          window.close();
        } else {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Token refresh logic - refresh every 40 minutes (before 60 min expiry)
let refreshInterval = null;

export const startTokenRefresh = () => {
  if (refreshInterval) clearInterval(refreshInterval);
  
  refreshInterval = setInterval(async () => {
    const sessionScoped = Boolean(sessionStorage.getItem('accessToken'));
    const storage = sessionScoped ? sessionStorage : localStorage;
    const token = storage.getItem('accessToken');
    if (token) {
      try {
        const response = await api.post('/auth/refresh');
        storage.setItem('accessToken', response.data.access_token);
        console.log('Token refreshed successfully');
      } catch (error) {
        console.error('Token refresh failed:', error);
        // Will be handled by 401 interceptor
      }
    }
  }, 40 * 60 * 1000); // 40 minutes
};

export const stopTokenRefresh = () => {
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }
};

// Chat API endpoints
export const chatAPI = {
  sendMessage: async ({ message, conversation_id, collection_id, history, agent_id, agent_config, workflow_context, customer_context }) => {
    const response = await api.post('/chat/message', {
      message,
      conversation_id,
      collection_id,
      history,
      agent_id,
      agent_config,
      workflow_context,
      customer_context
    });
    return response.data;
  },
  
  getConversations: async () => {
    const response = await api.get('/chat/conversations');
    return response.data;
  },
  
  getConversation: async (conversationId) => {
    const response = await api.get(`/chat/conversations/${conversationId}`);
    return response.data;
  },

  deleteConversation: async (conversationId) => {
    const response = await api.delete(`/chat/conversations/${conversationId}`);
    return response.data;
  },

  // Conversations browse — ALL conversations for the tenant (lead or not,
  // including anonymous widget visitors). Filters on Conversation.tenant_id.
  listAllConversations: async (params = {}) => {
    const response = await api.get('/chat/all-conversations', { params });
    return response.data;
  },

  getConversationTranscript: async (conversationId, params = {}) => {
    const response = await api.get(`/chat/all-conversations/${conversationId}`, { params });
    return response.data;
  },

  // Explicit ids, not a filter: "delete everything older than N days" in one
  // call has no undo and no confirmation of what it actually matched. The caller
  // sends rows it has seen and can check the returned count against what it
  // sent. Age-based purging on a schedule is a separate, deliberately slower
  // thing — see Conversation Retention in the backlog.
  bulkDeleteConversations: async (conversationIds, params = {}) => {
    const response = await api.post(
      '/chat/all-conversations/bulk-delete',
      { conversation_ids: conversationIds },
      { params }
    );
    return response.data;
  },

  // Hard-delete a conversation (admin-only, tenant-scoped). Leaves any captured
  // lead untouched (leads are deleted separately from Leads Management).
  deleteConversationRecord: async (conversationId, params = {}) => {
    const response = await api.delete(`/chat/all-conversations/${conversationId}`, { params });
    return response.data;
  },

  getChatConfig: async () => {
    const response = await api.get('/chat/config');
    return response.data;
  },
  
  captureLead: async (leadData) => {
    const response = await api.post('/chat/capture-lead', leadData);
    return response.data;
  }
};

// RAG API endpoints
export const ragAPI = {
  getStats: () => api.get('/rag/stats'),
  getCollections: () => api.get('/rag/collections'),
  getDocuments: (params) => api.get('/rag/documents', { params }),
  // Unified WATI-style Knowledge Sources view — one row per URL/file across
  // every collection the tenant owns, plus tenant-wide usage summary for the
  // "X of Y used" storage bar.
  getKnowledgeSources: () => api.get('/rag/sources'),
  // `source` goes in the query string, never a path segment: Container Apps
  // ingress merges the adjacent slashes of a URL carried in a path, which is
  // what makes the delete route 404 on real sources.
  getSourceText: (collectionId, source) =>
    api.get('/rag/sources/text', { params: { collection_id: collectionId, source } }),
  deleteDocument: (collectionId, documentName) => 
    api.delete(`/rag/collections/${collectionId}/documents/${encodeURIComponent(documentName)}`),
  deleteCollection: (id) => api.delete(`/rag/collections/${id}`),
  createCollection: (data) => api.post('/rag/collections', data),
  uploadDocument: (collectionId, formData) => 
    api.post(`/rag/collections/${collectionId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  search: (query, collectionId) => 
    api.post('/rag/search', { query, collection_id: collectionId }),
  clearDatabase: () => api.delete('/rag/clear'),
  // Web Scraper
  scrapeWebsite: (scrapeData) => api.post('/rag/scrape', scrapeData),
  getScraperConfig: () => api.get('/rag/scraper/config'),
  // Scrape Job Management (background processing)
  getScrapeJobStatus: (jobId) => api.get(`/rag/scrape/jobs/${jobId}`),
  refreshScrapeJob: (jobId, refreshData) =>
    api.post(`/rag/scrape/jobs/${jobId}/refresh`, refreshData),
  getJobPages: (jobId, statusFilter = null) =>
    api.get(`/rag/scrape/jobs/${jobId}/pages`, { params: { status_filter: statusFilter } }),
  getScrapePageContent: (jobId, pageId) =>
    api.get(`/rag/scrape/jobs/${jobId}/pages/${pageId}/content`),
  listScrapeJobs: (status = null, limit = 20) => 
    api.get('/rag/scrape/jobs', { params: { status, limit } }),
  resumeScrapeJob: (jobId) => api.post(`/rag/scrape/jobs/${jobId}/resume`),
  retryUrl: (jobId, url, rescrape = false) => api.post(`/rag/scrape/jobs/${jobId}/retry-url`, { url, rescrape }),
  cancelScrapeJob: (jobId) => api.delete(`/rag/scrape/jobs/${jobId}`),
};

// License / Plan Usage API
export const licenseAPI = {
  // Plan & Usage panel data — tier + 5 usage rows (conversations, documents,
  // storage, users, agents) against tier limits. Backend: license.py::get_tenant_usage.
  getUsage: () => api.get('/license/usage'),
};

// Configuration API endpoints
export const configAPI = {
  // Get complete configuration
  getActiveSetup: async () => {
    try {
      console.log('📡 Fetching active setup from /config/active-setup');
      const response = await api.get('/config/active-setup');
      console.log('📡 Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching active setup:', error);
      throw error;
    }
  },
  
  // Get active configuration only
  getActiveConfig: async () => {
    try {
      const response = await api.get('/config/active');
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching active config:', error);
      throw error;
    }
  },
  
  // Update active configuration
  updateActiveConfig: async (configData) => {
    try {
      console.log('📤 Updating active config:', configData);
      const response = await api.put('/config/active', configData);
      console.log('✅ Update response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating active config:', error);
      throw error;
    }
  },
  
  // Get settings
  getSettings: async () => {
    try {
      const response = await api.get('/config/settings');
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching settings:', error);
      throw error;
    }
  },
  
  // Update settings
  updateSettings: async (settingsData) => {
    try {
      console.log('📤 Updating settings:', settingsData);
      const response = await api.put('/config/settings', settingsData);
      console.log('✅ Update response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating settings:', error);
      throw error;
    }
  },
  
  // Switch setup type
  switchSetup: async (setupType) => {
    try {
      console.log('🔄 Switching setup to:', setupType);
      const response = await api.put('/config/setup-type', { setupType });
      console.log('✅ Switch response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error switching setup:', error);
      throw error;
    }
  },
  
  // Get vision models
  getVisionModels: async (setupType = null) => {
    try {
      const url = setupType ? `/config/vision-models?setup_type=${setupType}` : '/config/vision-models';
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching vision models:', error);
      throw error;
    }
  },

  // Get LLM models for agent selection
  getLLMModels: async (setupType = null) => {
    try {
      const url = setupType ? `/config/llm-models?setup_type=${setupType}` : '/config/llm-models';
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching LLM models:', error);
      throw error;
    }
  },
};

// Agent API endpoints (plural)
export const agentsAPI = {
  list: () => api.get('/agents'),
  getOnboardingStatus: () => api.get('/agents/onboarding-status'),
  // Websites each agent's widget has loaded on in the last 30 days.
  getInstallStatus: () => api.get('/agents/install-status'),
  getKnowledgePreview: (collection) => api.get('/agents/knowledge-preview', { params: { collection } }),
  getKnowledgeOptions: () => api.get('/agents/knowledge-options'),
  getCapacity: () => api.get('/agents/capacity'),
  get: (id) => api.get(`/agents/${id}`),
  create: (data) => api.post('/agents/', data),
  update: (id, data) => api.put(`/agents/${id}`, data),
  delete: (id) => api.delete(`/agents/${id}`),
  
  // Aliases for backward compatibility
  getAgents: () => api.get('/agents'),
  getAgent: (id) => api.get(`/agents/${id}`),
  getAgentByName: (name) => api.get(`/agents/by-name/${encodeURIComponent(name)}`),
  createAgent: (data) => api.post('/agents/', data),
  updateAgent: (id, data) => api.put(`/agents/${id}`, data),
  updateAgentBranding: (id, data) => api.put(`/agents/${id}/branding`, data),
  deleteAgent: (id) => api.delete(`/agents/${id}`),
  
  // Agent chat (supports abort signal for cancellation)
  chat: (id, data, config = {}) => api.post(`/agents/${id}/chat`, data, config),
  
  // Agent conversations (database-backed)
  getConversations: (id) => api.get(`/agents/${id}/conversations`),
  getConversation: (agentId, conversationId) => api.get(`/agents/${agentId}/conversations/${conversationId}`),
  createConversation: (agentId, data = {}) => api.post(`/agents/${agentId}/conversations`, data),
  updateConversation: (agentId, conversationId, data) => api.put(`/agents/${agentId}/conversations/${conversationId}`, data),
  deleteConversation: (agentId, conversationId) => api.delete(`/agents/${agentId}/conversations/${conversationId}`),
  
  // Agent testing
  testAgent: (id, message) => api.post(`/agents/${id}/test`, null, { params: { message } }),

  // Regenerate the agent's KB-grounded opener pills.
  // force=true bins every LLM pill and rebuilds from scratch, keeping the
  // admin's manual pills. Without it, regeneration can only add and prune —
  // so a pool that predates the quality gate keeps whatever the gate happens
  // to pass.
  regeneratePrompts: (id, { force = false } = {}) =>
    api.post(`/agents/${id}/regenerate-prompts${force ? '?force=true' : ''}`),

  // Prompt pool — structured JSON template that the widget consumes.
  // GET returns the full structured pool; PUT replaces it and sets
  // suggested_prompts_locked so auto-regen leaves manual edits alone.
  getPromptPool: (id) => api.get(`/agents/${id}/prompt-pool`),
  updatePromptPool: (id, pool) => api.put(`/agents/${id}/prompt-pool`, pool),
  
  // Defaults for a NEW agent. The backend owns these — the form must not invent
  // its own, or the backend's defaults can never apply (the client overrides
  // them on every create) and a default gets persisted as if it were a decision.
  getAgentDefaults: () => api.get('/agents/defaults'),

  // Models and providers
  getAvailableModels: () => api.get('/agents/models/available'),
  getProviders: () => api.get('/agents/providers/list'),
  
  // System prompts
  getSystemPrompts: () => api.get('/agents/system-prompts'),
  getSystemPrompt: (filename) => api.get(`/agents/system-prompts/${filename}`),
  createSystemPrompt: (promptData) => api.post('/agents/system-prompts', promptData),
  updateSystemPrompt: (filename, promptData) => api.put(`/agents/system-prompts/${filename}`, promptData),
  deleteSystemPrompt: (filename) => api.delete(`/agents/system-prompts/${filename}`),
  syncSystemPrompts: () => api.post('/agents/system-prompts/sync'),
  
  // Workflows
  getWorkflows: () => api.get('/agents/workflows'),
  getWorkflow: (workflowId) => api.get(`/agents/workflows/${workflowId}`),
  createWorkflow: (workflow) => api.post('/agents/workflows', workflow),
  updateWorkflow: (workflowId, workflow) => api.put(`/agents/workflows/${workflowId}`, workflow),
  deleteWorkflow: (workflowId) => api.delete(`/agents/workflows/${workflowId}`),
  
  // Settings
  getGlobalSettings: () => api.get('/agents/settings/global'),
  updateGlobalSettings: (settings) => api.put('/agents/settings/global', settings),
};

// Agent API endpoints (singular - for backward compatibility)
export const agentAPI = {
  getAgents: () => api.get('/agents'),
  getAgent: (id) => api.get(`/agents/${id}`),
  createAgent: (data) => api.post('/agents/', data),
  updateAgent: (id, data) => api.put(`/agents/${id}`, data),
  updateAgentBranding: (id, data) => api.put(`/agents/${id}/branding`, data),
  deleteAgent: (id) => api.delete(`/agents/${id}`),
};

// Auth API endpoints
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  requestEmailCode: (email, turnstileToken) => api.post('/auth/email/request-code', {
    email, ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
  }),
  verifyEmailCode: (email, code) => api.post('/auth/email/verify-code', { email, code }),
  verifySuperadminPassword: (email, stepUpToken, password) =>
    api.post('/auth/email/verify-superadmin-password', { email, step_up_token: stepUpToken, password }),
  requestSignupCode: (email, turnstileToken) => api.post('/auth/signup/request-code', {
    email, ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
  }),
  verifySignupCode: (payload) => api.post('/auth/signup/verify-code', payload),
  register: (userData) => api.post('/auth/register', userData),
  getCurrentUser: () => api.get('/auth/me'),
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },
};

// CRM API endpoints
export const crmAPI = {
  // Configuration Management
  getConfig: () => api.get('/crm/config'),
  updateConfig: (config) => api.put('/crm/config', config),
  updateCRMConfig: (crmType, config) => api.put(`/crm/config/${crmType}`, config),
  
  // Status and Connection
  getStatus: () => api.get('/crm/status'),
  configure: (crmType, config) => api.post('/crm/configure', { crm_type: crmType, config }),
  setActiveCRM: (crmName) => api.post(`/crm/set-active/${crmName}`),
  testConnection: (crmName) => api.get(`/crm/test/${crmName}`),
  
  // Customers
  getCustomers: (params = {}) => api.get('/crm/customers', { params }),
  getCustomer: (customerId, crm = null) => api.get(`/crm/customers/${customerId}`, { params: { crm } }),
  searchCustomers: (query, crm = null) => api.post('/crm/customers/search', { query, crm }),
  getCustomerContext: (customerId, crm = null) => api.get(`/crm/customers/${customerId}/context`, { params: { crm } }),
  lookupCustomerByEmail: (email, crm = null) => api.get('/crm/customers/lookup', { params: { email, crm } }),
  
  // Products
  getProducts: (params = {}) => api.get('/crm/products', { params }),
  
  // Tickets
  getTickets: (params = {}) => api.get('/crm/tickets', { params }),
  
  // Orders
  getOrders: (params = {}) => api.get('/crm/orders', { params }),
  
  // Data Sync
  syncData: (request = {}) => api.post('/crm/sync', request),
  getSyncStatus: () => api.get('/crm/sync/status'),
};

// DMS API endpoints
export const dmsAPI = {
  // Configuration Management
  getConfig: () => api.get('/dms/config'),
  updateConfig: (config) => api.put('/dms/config', config),
  updateDMSConfig: (dmsType, config) => api.put(`/dms/config/${dmsType}`, config),
  
  // Status and Connection
  getStatus: () => api.get('/dms/status'),
  setActiveDMS: (dmsType) => api.post(`/dms/set-active/${dmsType}`),
  testConnection: (dmsType) => api.post(`/dms/test/${dmsType}`),
  
  // Documents
  listDocuments: (params = {}) => api.get('/dms/documents', { params }),
  
  // Data Sync
  syncDocuments: (request = {}) => api.post('/dms/sync', request),
  getSyncStatus: () => api.get('/dms/sync/status'),
};

// Analytics API endpoints
export const analyticsAPI = {
  // Lead Management
  createLead: (leadData) => api.post('/analytics/leads', leadData),
  createManualLead: (leadData, params = {}) => api.post('/analytics/leads/manual', leadData, { params }),
  importLeadsCsv: (file, params = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/analytics/leads/import-csv', formData, {
      params,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getLeads: (params = {}) => api.get('/analytics/leads', { params }),
  exportLeadsCsv: (params = {}) => api.get('/analytics/leads/export.csv', { params, responseType: 'blob' }),
  // PRD-LEAD-025 — distinct campaign sources / names for the leads filter dropdowns.
  getLeadCampaigns: (params = {}) => api.get('/analytics/leads/campaigns', { params }),
  // AI Lead Intelligence — weekly priority-score tier breakdown (no LLM).
  getScoreTrends: (params = {}) => api.get('/analytics/leads/score-trends', { params }),
  // AI Lead Intelligence — single-call campaign brief (Azure OpenAI).
  generateCampaignBrief: (data, params = {}) => api.post('/analytics/leads/campaign-brief', data, { params }),
  // AI Lead Intelligence — pre-computed cross-channel conversation insights.
  getConversationInsightsSummary: (params = {}) => api.get('/analytics/conversation-insights/summary', { params }),
  // Missed Opportunity Intelligence (Scale) — sidebar badge count and full dashboard summary.
  getMissedOpportunitiesCount: (params = {}) => api.get('/analytics/missed-opportunities/count', { params }),
  getMissedOpportunitiesSummary: (params = {}) => api.get('/analytics/missed-opportunities/summary', { params }),
  // AI Lead Intelligence — latest week's AI-extracted intent themes.
  getIntentThemes: (params = {}) => api.get('/analytics/intent-themes', { params }),
  // Impact — WATI-inspired customer-facing hero KPIs (conversations, after-hours %,
  // leads captured, meetings booked) with month-over-month deltas.
  getImpactSummary: (params = {}) => api.get('/analytics/impact', { params }),
  // Impact — 4-stage lead funnel (visitors -> engaged -> qualified -> meetings).
  getImpactFunnel: (params = {}) => api.get('/analytics/impact/funnel', { params }),
  // Impact — top question themes with resolution %.
  getImpactTopQuestions: (params = {}) => api.get('/analytics/impact/top-questions', { params }),
  // Impact — WATI-style per-owner sales performance (leads assigned, deals won/lost, conversion).
  getImpactTeam: (params = {}) => api.get('/analytics/impact/team', { params }),
  getLead: (leadId) => api.get(`/analytics/leads/${leadId}`),
  updateLead: (leadId, data, params = {}) => api.put(`/analytics/leads/${leadId}`, data, { params }),
  updateLeadStatus: (leadId, status) => api.patch(`/analytics/leads/${leadId}/status`, { status }),
  deleteLead: (leadId) => api.delete(`/analytics/leads/${leadId}`),
  rescoreLead: (leadId) => api.post(`/analytics/leads/${leadId}/rescore`),

  // Lead Assignment & Notes
  assignLead: (leadId, data) => api.patch(`/analytics/leads/${leadId}/assign`, data),
  getTeamMembers: (params = {}) => api.get('/analytics/team-members', { params }),

  // Analytics Stats
  getStats: (days = 30, params = {}) => api.get('/analytics/stats', { params: { days, ...params } }),

  // Chatbot Analytics
  getChatbotAnalytics: (days = 30, params = {}) => api.get('/analytics/chatbots', { params: { days, ...params } }),

  // Public Chatbot Analytics (no auth required)
  getPublicChatbotAnalytics: (agentId, days = 30) => api.get(`/analytics/public/${agentId}`, { params: { days } }),
  getPublicBotAnalytics: (agentId, days = 30) => api.get(`/analytics/public/${agentId}`, { params: { days } }),
};

// Post-sale support tickets
export const supportAPI = {
  getMetrics: (params = {}) => api.get('/support/tickets/metrics', { params }),
  getInsights: (params = {}) => api.get('/support/tickets/insights', { params }),
  getTickets: (params = {}) => api.get('/support/tickets', { params }),
  getTicket: (ticketId, params = {}) => api.get(`/support/tickets/${ticketId}`, { params }),
  getTicketActivity: (ticketId, params = {}) => api.get(`/support/tickets/${ticketId}/activity`, { params }),
  addTicketNote: (ticketId, data, params = {}) => api.post(`/support/tickets/${ticketId}/notes`, data, { params }),
  createTicket: (data, params = {}) => api.post('/support/tickets', data, { params }),
  resolveTicket: (ticketId, data, params = {}) => api.post(`/support/tickets/${ticketId}/resolve`, data, { params }),
  updateTicket: (ticketId, data, params = {}) => api.patch(`/support/tickets/${ticketId}`, data, { params }),
  escalateTicket: (ticketId, data = {}, params = {}) => api.post(`/support/tickets/${ticketId}/escalate`, data, { params }),
  bulkUpdateTickets: (data, params = {}) => api.patch('/support/tickets/bulk', data, { params }),
  deleteTicket: (ticketId, params = {}) => api.delete(`/support/tickets/${ticketId}`, { params }),
  bulkDeleteTickets: (ticketIds, params = {}) => api.delete('/support/tickets/bulk', { params, data: { ticket_ids: ticketIds } }),
  exportTicketsCsv: (params = {}) => api.get('/support/tickets/export.csv', { params, responseType: 'blob' }),
  exportTicketsJson: (params = {}) => api.get('/support/tickets/export.json', { params }),
  // Tenant-scoped (even for superadmin) so the dropdown only offers valid assignees.
  getAssignableUsers: (params = {}) => api.get('/support/assignable-users', { params }),
  runReminders: (params = {}) => api.post('/support/reminders/run', null, { params }),
};

// Signup funnel and failures (superadmin-only on the server).
export const signupHealthAPI = {
  get: (params = {}) => api.get('/health/signups', { params }),
  // Junk attempts only: never touches accounts or sign-in codes.
  deleteAttempts: (emails) => api.post('/health/signups/delete', { emails }),
};

// Questions agents could not answer, grouped, with what to add (superadmin-only).
export const conversationAuditAPI = {
  get: (params = {}) => api.get('/health/conversation-audit', { params }),
};

// Public-site feedback review (superadmin-only on the server).
export const feedbackAPI = {
  list: (params = {}) => api.get('/feedback', { params }),
  updateStatus: (feedbackId, status) => api.patch(`/feedback/${feedbackId}`, { status }),
};

// Promote API endpoints (superadmin only)
export const billingAPI = {
  // Tenant admin paying for their OWN plan. Sends no tenant id: the server takes
  // it from the session, so a tenant cannot start a checkout billed to another.
  selfCheckout: (payload) => api.post('/billing/self-checkout', payload),
  listOwnAddons: () => api.get('/billing/self/addons'),
  // Plan, published prices and own payment history for the signed-in tenant.
  // /prices and /payments are superadmin-only, so this is the only way a
  // tenant admin can see what a plan costs or what they have already paid.
  getOwnBilling: () => api.get('/billing/self'),
  // Invoicing identity and place of supply. Tenant-scoped like the rest
  // of /self -- no tenant id, the server takes it from the session.
  saveOwnBillingDetails: (payload) => api.put('/billing/self/details', payload),
  // The business mobile anchors the billing currency (+91 is INR, any other
  // country code USD) until a payment or the back office fixes it.
  getOwnBusinessPhone: () => api.get('/billing/self/business-phone'),
  updateOwnBusinessPhone: (businessPhone) =>
    api.put('/billing/self/business-phone', { business_phone: businessPhone }),
  // Superadmin billing/payments configuration. Credential values are
  // write-only and go into one atomic Key Vault Test/Live bundle; neither
  // values nor vault URLs are returned to the browser.
  getProviders: () => api.get('/billing/providers'),
  createProvider: (data) => api.post('/billing/providers', data),
  upsertProvider: (provider, data) => api.put(`/billing/providers/${provider}`, data),
  activateProvider: (provider) => api.post(`/billing/providers/${provider}/activate`),
  deleteProvider: (provider) => api.delete(`/billing/providers/${provider}`),
  // Free and read-only: asks the gateway whether the stored credentials
  // authenticate. Answers 200 with ok:false when they do not.
  verifyProviderCredentials: (provider, mode) =>
    api.post(`/billing/providers/${provider}/verify-credentials`, { mode }),
  // Mints a real, small, disposable payment link. In live mode this moves
  // real money and is refunded from the gateway dashboard.
  createProviderTestPayment: (provider, mode, currency) =>
    api.post(`/billing/providers/${provider}/test-payment`, { mode, currency }),
  getPlans: () => api.get('/billing/plans'),
  updatePlan: (tier, data) => api.put(`/billing/plans/${tier}`, data),
  getPrices: () => api.get('/billing/prices'),
  upsertPrice: (tier, data) => api.put(`/billing/prices/${tier}`, data),
  getAddons: () => api.get('/billing/addons'),
  upsertAddon: (addon, data) => api.put(`/billing/addons/${addon}`, data),
  createPaymentLink: (data) => api.post('/billing/payment-link', data),
  // Whether Serviti charges GST at all. Superadmin-only: it is a fact
  // about the business, not a per-tenant setting.
  // Confirm payments whose webhook never arrived. An endpoint nobody can
  // reach is not a recovery path, so this is wired to a button.
  reconcilePayments: () => api.post('/billing/payments/reconcile'),
  getTaxSettings: () => api.get('/billing/tax-settings'),
  updateTaxSettings: (data) => api.put('/billing/tax-settings', data),
  getPayments: (limit = 50) => api.get('/billing/payments', { params: { limit } }),
};

export const promoteAPI = {
  getTenants: () => api.get('/promote/tenants'),
  promoteTenant: (tenantId) => api.post(`/promote/tenants/${tenantId}`),
  getStatus: (tenantId) => api.get(`/promote/tenants/${tenantId}/status`),
};

export default api;

